import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { Mail, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";

function NewsletterForm() {
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) {
      toast.error("Enter a valid email address");
      return;
    }
    setSubscribed(true);
    toast.success("You're subscribed — welcome aboard!");
  };

  if (subscribed) {
    return (
      <div className="flex w-full max-w-sm items-center gap-2 rounded-full bg-primary/10 px-5 py-3 text-sm font-semibold text-primary">
        <Check className="size-4 shrink-0" /> Thanks for subscribing!
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="flex w-full max-w-sm items-center gap-1.5 rounded-full border border-border/60 bg-card p-1.5 shadow-soft">
      <Mail className="ml-2.5 size-4 shrink-0 text-muted-foreground" />
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Your email address"
        aria-label="Email address"
        className="w-full min-w-0 bg-transparent text-sm outline-none placeholder:text-muted-foreground/70"
      />
      <button
        type="submit"
        className="flex shrink-0 items-center gap-1 rounded-full bg-primary px-4 py-2.5 text-xs font-bold text-primary-foreground transition-colors hover:bg-primary/90"
      >
        Subscribe <ArrowRight className="size-3.5" />
      </button>
    </form>
  );
}

export function Footer() {
  return (
    <footer className="border-t border-border bg-gradient-warm">
      <div className="mx-auto max-w-7xl px-4 pt-12 sm:px-6 lg:px-8">
        <div className="flex flex-col items-start gap-5 border-b border-border/60 pb-10 lg:flex-row lg:items-center lg:justify-between lg:gap-6">
          <div className="max-w-md">
            <h3 className="font-display text-xl font-semibold">Stay in the loop</h3>
            <p className="mt-1.5 text-sm text-muted-foreground">
              Get new venues, seasonal offers and event announcements in your inbox.
            </p>
          </div>
          <NewsletterForm />
        </div>
      </div>
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-10 sm:px-6 md:grid-cols-4 lg:px-8">
        <div>
          <Link to="/" className="flex items-center">
            <span className="font-[family-name:var(--font-brand)] text-3xl font-bold leading-none">Oyo<span className="text-primary italic">Booking</span></span>
          </Link>
          <p className="mt-4 max-w-xs text-sm text-muted-foreground">
            Premium hospitality and venues across Oyo State, Nigeria — discovered, compared and booked in minutes.
          </p>
        </div>

        <div>
          <h4 className="font-display text-sm font-semibold text-foreground">Discover</h4>
          <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
            <li><Link to="/hotels" className="transition-colors hover:text-primary">Hotels & Stays</Link></li>
            <li><Link to="/event-centres" className="transition-colors hover:text-primary">Event Centres</Link></li>
            <li><Link to="/open-spaces" className="transition-colors hover:text-primary">Open Spaces</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-display text-sm font-semibold text-foreground">Host</h4>
          <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
            <li><Link to="/list-your-property" className="transition-colors hover:text-primary">List your property</Link></li>
            <li><Link to="/about" className="transition-colors hover:text-primary">About us</Link></li>
            <li><Link to="/contact" className="transition-colors hover:text-primary">Contact us</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-display text-sm font-semibold text-foreground">Support</h4>
          <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
            <li><Link to="/contact" className="transition-colors hover:text-primary">Help centre</Link></li>
            <li><Link to="/forgot-password" className="transition-colors hover:text-primary">Reset password</Link></li>
            <li><Link to="/login" className="transition-colors hover:text-primary">Sign in</Link></li>
            <li><Link to="/signup" className="transition-colors hover:text-primary">Create account</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-border/60">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-2 px-4 pt-6 pb-24 text-xs text-muted-foreground sm:flex-row sm:px-6 sm:py-6 lg:px-8">
          <span>© {new Date().getFullYear()} OyoBooking. All rights reserved.</span>
          <div className="flex gap-4">
            <Link to="/about" className="transition-colors hover:text-primary">Privacy</Link>
            <Link to="/about" className="transition-colors hover:text-primary">Terms</Link>
            <Link to="/contact" className="transition-colors hover:text-primary">Support</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
